import { useState, useEffect, useCallback, useRef } from "react";
import type {
  Challenge,
  ChallengeResult,
  ChallengeState,
  ChallengeType,
  DisciplineSlug,
  CountingChallengeContent,
  AdditionChallengeContent,
  SubtractionChallengeContent,
  NumberRecognitionContent,
  LetterRecognitionContent,
  BeginningSoundContent,
  WordBuildingContent,
  PatternSequenceContent,
  ColorPatternContent,
  KeyFindingContent,
  SequenceTypingContent,
  WordTypingContent,
  SingleCommandContent,
} from "@jumpbean/shared";
import { validateAnswer } from "@jumpbean/shared/game-logic";
import { calculateStars } from "@jumpbean/shared";

import { ChallengeWrapper } from "./ChallengeWrapper";
import { ChallengeLoadingState } from "./ChallengeLoadingState";
import { CountingChallenge } from "./CountingChallenge";
import { MultipleChoiceChallenge } from "./MultipleChoiceChallenge";
import { SequenceChallenge } from "./SequenceChallenge";
import { DragDropChallenge } from "./DragDropChallenge";
import { ChildKeyboard } from "./ChildKeyboard";
import { AnswerFeedback } from "./AnswerFeedback";
import { Confetti } from "../Confetti";
import {
  ColorPatternGame,
  ShapePatternGame,
  PatternTrain,
  PATTERN_COLORS,
  PATTERN_SHAPES,
  type PatternColor,
  type PatternShape,
} from "./patterns";
import { MazeGame } from "./programming/MazeGame";

export interface ChallengeEngineProps {
  /** The challenge to render */
  challenge: Challenge;
  /** Called when challenge is completed */
  onComplete: (result: ChallengeResult) => void;
  /** Called when skip is pressed */
  onSkip?: () => void;
  /** Called when back button is pressed */
  onBack?: () => void;
  /** Character name to display */
  characterName?: string;
  /** Character image URL */
  characterImageUrl?: string;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Whether to show skip button */
  showSkip?: boolean;
}

/**
 * ChallengeEngine - The core game engine component
 *
 * Receives challenge data, determines the challenge type,
 * renders the appropriate challenge component, and manages
 * challenge state (loading, ready, answering, completed).
 */
export function ChallengeEngine({
  challenge,
  onComplete,
  onSkip,
  onBack,
  characterName,
  characterImageUrl,
  discipline = "math",
  showSkip = false,
}: ChallengeEngineProps) {
  // Challenge state management
  const [challengeState, setChallengeState] = useState<ChallengeState>("loading");
  const [selectedAnswer, setSelectedAnswer] = useState<unknown>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [starsEarned, setStarsEarned] = useState<1 | 2 | 3>(1);

  // Timer tracking
  const startTimeRef = useRef<number>(Date.now());
  const timeSpentRef = useRef<number>(0);

  // Reset state when challenge changes
  useEffect(() => {
    setChallengeState("loading");
    setSelectedAnswer(null);
    setIsSubmitted(false);
    setIsCorrect(false);
    setAttempts(0);
    setShowCelebration(false);
    setShowFeedback(false);
    setStarsEarned(1);
    startTimeRef.current = Date.now();
    timeSpentRef.current = 0;

    // Simulate loading (could be used for audio preload, etc.)
    const timer = setTimeout(() => {
      setChallengeState("ready");
    }, 500);

    return () => clearTimeout(timer);
  }, [challenge.id]);

  // Handle answer selection
  const handleAnswer = useCallback((answer: unknown) => {
    setSelectedAnswer(answer);
    setChallengeState("answering");
  }, []);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (selectedAnswer === null) return;

    const newAttemptCount = attempts + 1;
    setIsSubmitted(true);
    setAttempts(newAttemptCount);

    // Validate the answer using the shared validation function
    const { validation, feedback } = validateAnswer(challenge, selectedAnswer, newAttemptCount);
    setIsCorrect(validation.isCorrect);

    // Show the feedback overlay
    setShowFeedback(true);

    if (validation.isCorrect) {
      // Calculate time spent
      timeSpentRef.current = Math.floor((Date.now() - startTimeRef.current) / 1000);

      // Stars are based purely on attempts (per issue #29)
      const stars = calculateStars(newAttemptCount);
      setStarsEarned(stars);

      // Show celebration confetti
      setShowCelebration(true);
      setChallengeState("completed");
    }
    // For incorrect answers, we let the AnswerFeedback component handle the timeout
  }, [selectedAnswer, challenge, attempts]);

  // Handle feedback completion (when feedback overlay animates out)
  const handleFeedbackComplete = useCallback(() => {
    setShowFeedback(false);

    if (isCorrect) {
      // Challenge completed - notify parent
      onComplete({
        challengeId: challenge.id,
        starsEarned: starsEarned,
        attempts: attempts,
        timeSpentSeconds: timeSpentRef.current,
        correct: true,
      });
    } else {
      // Incorrect answer - allow retry (unlimited retries per issue #29)
      setIsSubmitted(false);
      setSelectedAnswer(null);
      setChallengeState("ready");
    }
  }, [isCorrect, challenge.id, starsEarned, attempts, onComplete]);

  // Handle skip
  const handleSkip = useCallback(() => {
    timeSpentRef.current = Math.floor((Date.now() - startTimeRef.current) / 1000);
    onSkip?.();
  }, [onSkip]);

  // Show loading state
  if (challengeState === "loading") {
    return (
      <ChallengeLoadingState
        discipline={discipline}
        characterName={characterName}
        message="Get ready!"
      />
    );
  }

  // Get prompt text from challenge content
  const promptText = getPromptText(challenge);

  // Determine if submit should be disabled
  const submitDisabled = selectedAnswer === null || isSubmitted;

  // Generate screen reader announcement for current state
  const getScreenReaderAnnouncement = () => {
    if (showFeedback) {
      if (isCorrect) {
        return `Correct! You earned ${starsEarned} ${starsEarned === 1 ? 'star' : 'stars'}. Great job!`;
      } else {
        return `Not quite right. Try again! You can do it!`;
      }
    }
    return '';
  };

  return (
    <>
      {/* Screen reader announcements for game state changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {getScreenReaderAnnouncement()}
      </div>

      <ChallengeWrapper
        characterName={characterName}
        characterImageUrl={characterImageUrl}
        discipline={discipline}
        promptText={promptText}
        submitText={isSubmitted ? (isCorrect ? "Correct!" : "Try Again!") : "Check Answer"}
        submitDisabled={submitDisabled}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
        showSkip={showSkip && !isSubmitted}
        onBack={onBack}
        showCelebration={showCelebration}
      >
        {renderChallenge({
          challenge,
          discipline,
          selectedAnswer,
          isSubmitted,
          isCorrect,
          onAnswer: handleAnswer,
        })}
      </ChallengeWrapper>

      {/* Answer feedback overlay */}
      <AnswerFeedback
        isVisible={showFeedback}
        isCorrect={isCorrect}
        attempts={attempts}
        starsEarned={starsEarned}
        onComplete={handleFeedbackComplete}
      />

      {/* Celebration confetti overlay */}
      <Confetti isActive={showCelebration} count={60} duration={2000} />
    </>
  );
}

/**
 * Get prompt text from challenge content
 */
function getPromptText(challenge: Challenge): string {
  const content = challenge.content as Record<string, unknown>;

  // Check for explicit prompt text
  if (typeof content.promptText === "string") {
    return content.promptText;
  }

  // Generate default prompts based on challenge type
  const defaultPrompts: Record<ChallengeType, string> = {
    counting: "Count the objects!",
    number_recognition: "Find the number!",
    addition: "What is the sum?",
    subtraction: "What is the difference?",
    comparison: "Which side has more?",
    beginning_sound: "What letter does this start with?",
    letter_recognition: "Find the letter!",
    word_building: "Spell the word!",
    pattern_sequence: "Complete the pattern!",
    color_pattern: "What comes next?",
    key_finding: "Find the key on the keyboard!",
    sequence_typing: "Type the sequence!",
    word_typing: "Type the word!",
    maze: "Help me reach the goal!",
    single_command: "Help Pixel reach the star!",
  };

  return defaultPrompts[challenge.type] || "Complete the challenge!";
}

/**
 * Helper to cast content with proper type narrowing
 */
function asContent<T>(content: unknown): T {
  return content as T;
}

/**
 * Render the appropriate challenge component based on type
 */
function renderChallenge({
  challenge,
  discipline,
  selectedAnswer,
  isSubmitted,
  isCorrect,
  onAnswer,
}: {
  challenge: Challenge;
  discipline: DisciplineSlug;
  selectedAnswer: unknown;
  isSubmitted: boolean;
  isCorrect: boolean;
  onAnswer: (answer: unknown) => void;
}) {
  const content = challenge.content;

  switch (challenge.type) {
    case "counting": {
      const countingContent = asContent<CountingChallengeContent>(content);
      return (
        <CountingChallenge
          content={countingContent}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as number | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "number_recognition": {
      const numContent = asContent<NumberRecognitionContent>(content);
      return (
        <MultipleChoiceChallenge<number>
          content={{
            question: `Find the number ${numContent.targetNumber}`,
            options: numContent.options,
            correctAnswer: numContent.correctAnswer,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as number | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "addition": {
      const addContent = asContent<AdditionChallengeContent>(content);
      return (
        <MultipleChoiceChallenge<number>
          content={{
            question: `${addContent.operand1} + ${addContent.operand2} = ?`,
            options: addContent.options,
            correctAnswer: addContent.operand1 + addContent.operand2,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as number | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "subtraction": {
      const subContent = asContent<SubtractionChallengeContent>(content);
      return (
        <MultipleChoiceChallenge<number>
          content={{
            question: `${subContent.operand1} - ${subContent.operand2} = ?`,
            options: subContent.options,
            correctAnswer: subContent.operand1 - subContent.operand2,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as number | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "letter_recognition": {
      const letterContent = asContent<LetterRecognitionContent>(content);
      return (
        <MultipleChoiceChallenge<string>
          content={{
            question: `Find the letter "${letterContent.targetLetter}"`,
            options: letterContent.options,
            correctAnswer: letterContent.correctAnswer ?? letterContent.targetLetter,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as string | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "beginning_sound": {
      const soundContent = asContent<BeginningSoundContent>(content);
      return (
        <MultipleChoiceChallenge<string>
          content={{
            imageUrl: soundContent.imageUrl,
            question: `What letter does "${soundContent.word}" start with?`,
            options: soundContent.options,
            correctAnswer: soundContent.correctAnswer,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as string | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "word_building": {
      const wordContent = asContent<WordBuildingContent>(content);
      return (
        <DragDropChallenge
          content={{
            items: wordContent.availableLetters.map((letter, index) => ({
              id: `${letter}-${index}`,
              label: letter,
            })),
            dropZones: wordContent.correctAnswer.split("").map((_, index) => ({
              id: `slot-${index}`,
              hint: "_",
            })),
            correctArrangement: Object.fromEntries(
              wordContent.correctAnswer.split("").map((letter, index) => {
                const letterIndex = wordContent.availableLetters.findIndex(
                  (l) => l === letter
                );
                return [`slot-${index}`, `${letter}-${letterIndex}`];
              })
            ),
            instructions: `Spell: ${wordContent.correctAnswer}`,
          }}
          discipline={discipline}
          onAnswer={(arrangement) => {
            const word = Object.entries(arrangement)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([, itemId]) => itemId.split("-")[0])
              .join("");
            onAnswer(word);
          }}
          currentArrangement={
            selectedAnswer ? parseWordArrangement(selectedAnswer as string) : {}
          }
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "pattern_sequence": {
      const patternContent = asContent<PatternSequenceContent>(content);

      // Detect pattern type based on the pattern elements
      const patternType = detectPatternType(patternContent.pattern);

      // Use specialized Pattern components for the patterns discipline
      if (discipline === "patterns") {
        // Check if this should use the train theme (based on challenge metadata or random)
        const useTrainTheme =
          (patternContent as unknown as { theme?: string }).theme === "train";

        if (useTrainTheme) {
          return (
            <PatternTrain
              pattern={patternContent.pattern as string[]}
              missingIndex={patternContent.missingIndex}
              options={patternContent.options as string[]}
              correctAnswer={patternContent.correctAnswer as string}
              patternType={patternType}
              onAnswer={(answer) => onAnswer(answer)}
              selectedAnswer={selectedAnswer as string | null}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
            />
          );
        }

        if (patternType === "color") {
          return (
            <ColorPatternGame
              pattern={patternContent.pattern as PatternColor[]}
              missingIndex={patternContent.missingIndex}
              options={patternContent.options as PatternColor[]}
              correctAnswer={patternContent.correctAnswer as PatternColor}
              onAnswer={(answer) => onAnswer(answer)}
              selectedAnswer={selectedAnswer as PatternColor | null}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
            />
          );
        }

        if (patternType === "shape") {
          return (
            <ShapePatternGame
              pattern={patternContent.pattern as PatternShape[]}
              missingIndex={patternContent.missingIndex}
              options={patternContent.options as PatternShape[]}
              correctAnswer={patternContent.correctAnswer as PatternShape}
              onAnswer={(answer) => onAnswer(answer)}
              selectedAnswer={selectedAnswer as PatternShape | null}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
            />
          );
        }

        // Mixed or unknown pattern type - use PatternTrain as it handles both
        return (
          <PatternTrain
            pattern={patternContent.pattern as string[]}
            missingIndex={patternContent.missingIndex}
            options={patternContent.options as string[]}
            correctAnswer={patternContent.correctAnswer as string}
            patternType={patternType}
            onAnswer={(answer) => onAnswer(answer)}
            selectedAnswer={selectedAnswer as string | null}
            isSubmitted={isSubmitted}
            isCorrect={isCorrect}
          />
        );
      }

      // For non-patterns disciplines, use the generic SequenceChallenge
      return (
        <SequenceChallenge
          content={{
            pattern: patternContent.pattern,
            missingIndex: patternContent.missingIndex,
            options: patternContent.options,
            correctAnswer: patternContent.correctAnswer,
          }}
          discipline={discipline}
          onAnswer={onAnswer}
          selectedAnswer={selectedAnswer as string | number | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "color_pattern": {
      const colorPatternContent = asContent<ColorPatternContent>(content);
      // Handle snake_case from database
      const correctAnswer = colorPatternContent.correctAnswer ?? colorPatternContent.correct_answer;
      // For color_pattern, the last item in the pattern is where we're asking "what comes next?"
      // So we treat the pattern length as the missing index
      return (
        <ColorPatternGame
          pattern={colorPatternContent.pattern as PatternColor[]}
          missingIndex={colorPatternContent.pattern.length}
          options={colorPatternContent.options as PatternColor[]}
          correctAnswer={correctAnswer as PatternColor}
          onAnswer={(answer) => onAnswer(answer)}
          selectedAnswer={selectedAnswer as PatternColor | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "key_finding": {
      const keyContent = asContent<KeyFindingContent>(content);
      return (
        <ChildKeyboard
          content={keyContent}
          discipline={discipline}
          onAnswer={(key) => onAnswer(key)}
          selectedAnswer={selectedAnswer as string | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "sequence_typing": {
      const seqContent = asContent<SequenceTypingContent>(content);
      return (
        <ChildKeyboard
          content={seqContent}
          discipline={discipline}
          onAnswer={(sequence) => onAnswer(sequence)}
          selectedAnswer={selectedAnswer as string | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "word_typing": {
      const wordContent = asContent<WordTypingContent>(content);
      return (
        <ChildKeyboard
          content={wordContent}
          discipline={discipline}
          onAnswer={(word) => onAnswer(word)}
          selectedAnswer={selectedAnswer as string | null}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
        />
      );
    }

    case "maze": {
      const mazeContent = asContent<{
        gridSize?: [number, number];
        grid_size?: [number, number];
        start: [number, number];
        end: [number, number];
        obstacles: [number, number][];
        collectibles?: Array<{ position: [number, number]; type: "star" | "gem" | "gear" | "chip" }>;
      }>(content);
      const gridSize = mazeContent.gridSize ?? mazeContent.grid_size ?? [5, 5];
      return (
        <MazeGame
          size={gridSize}
          start={mazeContent.start}
          end={mazeContent.end}
          obstacles={mazeContent.obstacles}
          collectibles={mazeContent.collectibles}
          onAnswer={(commands) => onAnswer(commands)}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          discipline={discipline}
        />
      );
    }

    case "single_command": {
      const singleContent = asContent<SingleCommandContent>(content);
      const gridSize = singleContent.gridSize ?? singleContent.grid_size ?? [3, 3];
      return (
        <MazeGame
          size={gridSize}
          start={singleContent.start}
          end={singleContent.end}
          obstacles={singleContent.obstacles}
          collectibles={singleContent.collectibles?.map((pos) => ({ position: pos, type: "star" }))}
          onAnswer={(commands) => onAnswer(commands)}
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          discipline={discipline}
        />
      );
    }

    default:
      return (
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
          <p className="font-display text-body-xl text-neutral-600">
            Unknown challenge type: {challenge.type}
          </p>
        </div>
      );
  }
}

/**
 * Parse a word string back into arrangement format
 */
function parseWordArrangement(word: string): Record<string, string> {
  return Object.fromEntries(
    word.split("").map((letter, index) => [`slot-${index}`, `${letter}-0`])
  );
}

/**
 * Detect the type of pattern based on its elements
 * Returns "color", "shape", or "mixed"
 */
function detectPatternType(
  pattern: Array<string | number>
): "color" | "shape" | "mixed" {
  const colorKeys = Object.keys(PATTERN_COLORS);
  const shapeKeys = Object.keys(PATTERN_SHAPES);

  let hasColors = false;
  let hasShapes = false;

  for (const element of pattern) {
    if (typeof element === "string") {
      if (colorKeys.includes(element)) {
        hasColors = true;
      } else if (shapeKeys.includes(element)) {
        hasShapes = true;
      }
    }
  }

  if (hasColors && hasShapes) {
    return "mixed";
  }
  if (hasColors) {
    return "color";
  }
  if (hasShapes) {
    return "shape";
  }

  // Default to mixed for unknown patterns
  return "mixed";
}

export default ChallengeEngine;
